import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

declare function getLocatedElement(locatedElement?: ILocatedElement | string): Element | null;
declare function elementScrollTo(element: Element, x: number, y: number): void;

export const scroll = function (
    elementToScrollOn: ILocatedElement | undefined,
    elementToScrollTo: ILocatedElement | undefined,
    isScrollToElement: boolean,
    isDynamicScroll: boolean,
    expectedX: number,
    expectedY: number,
    shouldScrollLeft: boolean | undefined,
    shouldScrollTop: boolean | undefined,
) {
    function doScroll(expectedPosition: { x: number; y: number }, element: Element) {
        if (!element) {
            return { success: false };
        }

        elementScrollTo(element, expectedPosition.x, expectedPosition.y);

        const actualX = element.scrollLeft;
        const actualY = element.scrollTop;

        const success = (Math.abs(actualY - expectedPosition.y) < 1) && (Math.abs(actualX - expectedPosition.x) < 1);
        return { success, actualX, actualY };
    }

    function getExpectedPosition(parentElement: Element, locatedElement: ILocatedElement | undefined) {
        if (!isScrollToElement) {
            return { x: expectedX, y: expectedY };
        }

        const target = getLocatedElement(locatedElement);

        if (isDynamicScroll && !target) {
            return { x: parentElement.scrollWidth, y: parentElement.scrollHeight };
        }

        if (!target) {
            throw new Error('could not find target element');
        }

        const targetRect = target.getBoundingClientRect();


        let yScroll = 0;
        let xScroll = 0;
        const MAX_HEIGHT = Math.max(window.innerHeight - (targetRect.height + 10), 0);
        const MAX_WIDTH = Math.max(window.innerWidth - (targetRect.width + 10), 0);

        yScroll = shouldScrollTop ?
            parentElement.scrollTop + targetRect.top - Math.min(expectedY, MAX_HEIGHT) :
            parentElement.scrollTop;

        xScroll = shouldScrollLeft ?
            parentElement.scrollLeft + targetRect.left - Math.min(expectedX, MAX_WIDTH) :
            parentElement.scrollLeft;

        return { x: Math.round(xScroll), y: Math.round(yScroll) };
    }

    const isDocument = !elementToScrollOn;
    let elementToScrollOnEl = !isDocument ? getLocatedElement(elementToScrollOn) : (document.scrollingElement || document.documentElement);

    if (!elementToScrollOnEl) {
        throw new Error('could not find target to scroll on');
    }
    const positionBeforeScroll = { top: elementToScrollOnEl.scrollTop, left: elementToScrollOnEl.scrollLeft };
    let expectedPosition = getExpectedPosition(elementToScrollOnEl, elementToScrollTo);

    let result = doScroll(expectedPosition, elementToScrollOnEl);

    if (isDocument && !document.scrollingElement && !result.success && positionBeforeScroll.top === elementToScrollOnEl.scrollTop && positionBeforeScroll.left === elementToScrollOnEl.scrollLeft) {
        elementToScrollOnEl = document.body;
        expectedPosition = getExpectedPosition(elementToScrollOnEl, elementToScrollTo);
        result = doScroll(expectedPosition, elementToScrollOnEl);
    }

    let actualXString = result.actualX;
    let actualYString = result.actualY;
    const actualScrollToElement = getLocatedElement(elementToScrollTo);

    if (isScrollToElement && isDynamicScroll && !actualScrollToElement) {
        return { success: false, expectedPosition };
    }

    if (isScrollToElement) {
        if (!actualScrollToElement) {
            throw new Error('could not find target to scroll to');
        }
        const rect = actualScrollToElement.getBoundingClientRect();
        actualXString = rect.left;
        actualYString = rect.top;
    }
    return { success: result.success, actualX: actualXString, actualY: actualYString };
};
